import { use, useEffect } from 'react';

import { EmbeddedSurfaceContext } from '@/context/EmbeddedSurfaceContext';

/**
 * Hook to set the document title with appropriate formatting
 * @param title The page title to display
 * @param moduleOrOptions Optional module name, or options object
 */
interface UseDocumentTitleOptions {
  module?: string;
  suffix?: string;
  loading?: boolean;
}

export function useDocumentTitle(
  title: string | undefined,
  moduleOrOptions?: string | UseDocumentTitleOptions,
) {
  const moduleName = typeof moduleOrOptions === 'string'
    ? moduleOrOptions
    : moduleOrOptions?.module || moduleOrOptions?.suffix;
  const isLoading = typeof moduleOrOptions === 'object' && Boolean(moduleOrOptions?.loading);
  const isEmbedded = use(EmbeddedSurfaceContext);

  useEffect(() => {
    // Embedded surfaces share the host tab — its title belongs to the host page.
    if (isEmbedded) {
      return;
    }

    if (isLoading) {
      document.title = 'Loading... | Scrambl';
      return;
    }

    // If no title provided, use default app name
    if (!title) {
      document.title = 'Scrambl';
      return;
    }

    // Format: "Record Name | Scrambl" or "Record Name | Module | Scrambl"
    const formattedTitle = moduleName
      ? `${title} | ${moduleName} | Scrambl`
      : `${title} | Scrambl`;
      
    document.title = formattedTitle;
    
    // Cleanup - reset to default when component unmounts
    return () => {
      document.title = 'Scrambl';
    };
  }, [isEmbedded, isLoading, moduleName, title]);
}
